import React, { useEffect, useState } from "react";
import AdminMenu from "./AdminMenu";
import AdminHeader from "./AdminHeader";
import axios from "axios";
import {ADMIN_API_BASE} from "../../utils/apiConfig";

const formatDuration = (ms) => {
    if (!ms || ms <= 0) return "0s";

    const totalSeconds = Math.round(ms / 1000);

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }

    if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
    }

    return `${seconds}s`;
};

const formatNumber = (value) => {
    return Number(value || 0).toLocaleString();
};

const formatPercent = (value) => {
    return `${(Number(value || 0) * 100).toFixed(0)}%`;
};

const AdminAnalytics = () => {

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const getCsrfToken = async () => {
        const response = await fetch(`${ADMIN_API_BASE}/csrf-token`, {
            method: "GET",
            credentials: "include",
        });

        const data = await response.json();
        return data.csrfToken;
    };

    useEffect(() => {
        const loadAnalytics = async () => {
            try {
                setLoading(true);

                const token = await getCsrfToken();

                const response = await axios.get(`${ADMIN_API_BASE}/analytics`, {
                    headers: {
                        "csrf-token": token,
                    },
                    withCredentials: true,
                });

                const result = response.data;

                if (!result.success) {
                    throw new Error(
                        result.message || "Failed to load analytics"
                    );
                }

                setData(result);

            } catch (err) {
                console.error("Analytics error:", err);
                setError(
                    err.response?.data?.message ||
                    err.message ||
                    "Failed to load analytics"
                );
            } finally {
                setLoading(false);
            }
        };

        loadAnalytics();
    }, []);

    if (loading) {
        return (
            <div className="flex flex-1 bg-gray-50 min-h-screen">
                <div className="w-1/5">
                    <AdminMenu path="./" />
                </div>

                <div className="flex-grow w-4/5">
                    <AdminHeader title="داشبورد" />

                    <div className="p-8 flex items-center justify-center">
                        <p className="text-gray-500">
                            در حال بارگذاری...
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-1 bg-gray-50 min-h-screen">
                <div className="w-1/5">
                    <AdminMenu path="./" />
                </div>

                <div className="flex-grow w-4/5">
                    <AdminHeader title="داشبورد" />

                    <div className="p-8">
                        <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-5">
                            خطا در دریافت اطلاعات:
                            <br />
                            {error}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const stats = [
        {
            title: "DAU",
            value: formatNumber(data.stats.dau),
            color: "text-blue-600"
        },
        {
            title: "WAU",
            value: formatNumber(data.stats.wau),
            color: "text-green-600"
        },
        {
            title: "MAU",
            value: formatNumber(data.stats.mau),
            color: "text-purple-600"
        },
        {
            title: "Avg Session",
            value: formatDuration(
                data.stats.avg_session_duration
            ),
            color: "text-orange-600"
        }
    ];

    const metrics = [
        {
            title: "Average Lesson Level",
            value: Number(
                data.metrics.avg_lesson_level
            ).toFixed(1)
        },
        {
            title: "Median Lesson Level",
            value: Number(
                data.metrics.median_lesson_level
            ).toFixed(0)
        },
        {
            title: "Lesson Completion",
            value: formatPercent(
                data.metrics.lesson_completion_rate
            )
        },
        {
            title: "Average Mistakes",
            value: Number(
                data.metrics.avg_mistakes
            ).toFixed(1)
        },
        {
            title: "Average Duration",
            value: formatDuration(
                data.metrics.avg_lesson_duration
            )
        },
        {
            title: "Abandon Rate",
            value: formatPercent(
                data.metrics.abandon_rate
            )
        }
    ];

    return (
        <div className="flex flex-1 bg-gray-50 min-h-screen">

            <div className="w-1/5">
                <AdminMenu path="./" />
            </div>

            <div className="flex-grow w-4/5">

                <AdminHeader title="داشبورد" />

                <div className="p-8 space-y-8">

                    {/* Top Stats */}

                    <div className="grid grid-cols-4 gap-6">

                        {stats.map((item) => (
                            <div
                                key={item.title}
                                className="bg-white rounded-2xl shadow-sm border p-6"
                            >
                                <p className="text-gray-500 text-sm">
                                    {item.title}
                                </p>

                                <h2
                                    className={`text-4xl font-bold mt-3 ${item.color}`}
                                >
                                    {item.value}
                                </h2>
                            </div>
                        ))}

                    </div>


                    {/* Metrics */}

                    <div className="grid grid-cols-3 gap-6">

                        {metrics.map((item) => (
                            <div
                                key={item.title}
                                className="bg-white rounded-2xl shadow-sm border p-5"
                            >
                                <p className="text-gray-500">
                                    {item.title}
                                </p>

                                <h3 className="text-3xl font-bold mt-2">
                                    {item.value}
                                </h3>
                            </div>
                        ))}

                    </div>


                    {/* Charts */}

                    <div className="grid grid-cols-2 gap-6">

                        <div className="bg-white rounded-2xl shadow-sm border p-6">

                            <h2 className="font-semibold text-lg mb-5">
                                Lesson Attempts
                            </h2>

                            <div className="h-72 flex items-end gap-1 border-b border-l p-4">

                                {data.charts.lesson_attempts.map(
                                    (item) => {

                                        const max = Math.max(
                                            ...data.charts.lesson_attempts.map(
                                                x => Number(x.attempts)
                                            ),
                                            1
                                        );

                                        const height =
                                            (Number(item.attempts) / max) * 100;

                                        return (
                                            <div
                                                key={item.date}
                                                className="flex-1 flex items-end h-full"
                                                title={`${item.date}: ${item.attempts}`}
                                            >
                                                <div
                                                    className="w-full bg-blue-500 rounded-t"
                                                    style={{
                                                        height: `${height}%`
                                                    }}
                                                />
                                            </div>
                                        );
                                    }
                                )}

                            </div>

                        </div>


                        <div className="bg-white rounded-2xl shadow-sm border p-6">

                            <h2 className="font-semibold text-lg mb-5">
                                Daily Active Users
                            </h2>

                            <div className="h-72 flex items-end gap-1 border-b border-l p-4">

                                {data.charts.dau.map(
                                    (item) => {

                                        const max = Math.max(
                                            ...data.charts.dau.map(
                                                x => Number(x.users)
                                            ),
                                            1
                                        );

                                        const height =
                                            (Number(item.users) / max) * 100;

                                        return (
                                            <div
                                                key={item.date}
                                                className="flex-1 flex items-end h-full"
                                                title={`${item.date}: ${item.users}`}
                                            >
                                                <div
                                                    className="w-full bg-green-500 rounded-t"
                                                    style={{
                                                        height: `${height}%`
                                                    }}
                                                />
                                            </div>
                                        );
                                    }
                                )}

                            </div>

                        </div>

                    </div>


                    {/* Tables */}

                    <div className="grid grid-cols-2 gap-6">

                        {/* Hardest Lessons */}

                        <div className="bg-white rounded-2xl shadow-sm border">

                            <div className="p-5 border-b font-semibold">
                                Hardest Lessons
                            </div>

                            <table className="w-full">

                                <thead className="text-left text-gray-500 text-sm">

                                <tr>
                                    <th className="p-4">
                                        Lesson
                                    </th>

                                    <th className="p-4">
                                        Avg Mistakes
                                    </th>
                                </tr>

                                </thead>

                                <tbody>

                                {data.hardest_lessons.map(
                                    (item) => (

                                        <tr
                                            key={item.content_id}
                                            className="border-t"
                                        >
                                            <td className="p-4">
                                                {item.lesson}
                                            </td>

                                            <td className="p-4 font-semibold">
                                                {item.mistakes}
                                            </td>
                                        </tr>

                                    )
                                )}

                                </tbody>

                            </table>

                        </div>


                        {/* Most Active Courses */}

                        <div className="bg-white rounded-2xl shadow-sm border">

                            <div className="p-5 border-b font-semibold">
                                Most Active Courses
                            </div>

                            <table className="w-full">

                                <thead className="text-left text-gray-500 text-sm">

                                <tr>
                                    <th className="p-4">
                                        Course
                                    </th>

                                    <th className="p-4">
                                        Users
                                    </th>
                                </tr>

                                </thead>

                                <tbody>

                                {data.top_courses.map(
                                    (item) => (

                                        <tr
                                            key={item.course_id}
                                            className="border-t"
                                        >
                                            <td className="p-4">
                                                {item.course}
                                            </td>

                                            <td className="p-4 font-semibold">
                                                {formatNumber(
                                                    item.users
                                                )}
                                            </td>
                                        </tr>

                                    )
                                )}

                                </tbody>

                            </table>

                        </div>

                    </div>

                </div>

            </div>

        </div>
    );
};

export default AdminAnalytics;